import type { CanvasBackgroundTheme } from "../BackgroundAnimations/canvasBackgroundTheme";
import { SAFE_MAX_CANVAS_DIMENSION } from "../BackgroundAnimations/resolveCanvasSurfaceSize";

import type { BurstType } from "./Burst";
import {
  advanceThemedParticle,
  type BurstOrigin,
  createThemedParticle,
  drawThemedParticle,
  THEMED_BURST_COUNT,
  type ThemedBurstParticle,
  type ThemedBurstType,
} from "./themedBurstParticles";

type ParticleRenderType = "heart" | "sparkle" | "note" | "card" | "die" | "node";

type ColorFn = (alpha: number) => string;

const SUITS = ["\u2660", "\u2665", "\u2666", "\u2663"];
const NOTE_CHARS = ["\u2669", "\u266A", "\u266B", "\u266C"];
const DOT_OFFSETS: Record<number, [number, number][]> = {
  1: [[0, 0]],
  2: [
    [-1, -1],
    [1, 1],
  ],
  3: [
    [-1, -1],
    [0, 0],
    [1, 1],
  ],
  4: [
    [-1, -1],
    [1, -1],
    [-1, 1],
    [1, 1],
  ],
  5: [
    [-1, -1],
    [1, -1],
    [0, 0],
    [-1, 1],
    [1, 1],
  ],
  6: [
    [-1, -1],
    [1, -1],
    [-1, 0],
    [1, 0],
    [-1, 1],
    [1, 1],
  ],
};
const TYPE_SIZE: Record<ParticleRenderType, [number, number]> = {
  heart: [6, 12],
  sparkle: [4, 9],
  note: [14, 22],
  card: [18, 26],
  die: [16, 22],
  node: [2.5, 3.8],
};

const CONNECTOR_MAX_DISTANCE = 90;
const PEAK_ALPHA = 0.92;

interface BurstParticle {
  type: ParticleRenderType;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  rot: number;
  rotV: number;
  wob: number;
  wobAmp: number;
  life: number;
  maxLife: number;
  fadeStart: number;
  suit: string;
  face: number;
  char: string;
  dL: number;
  dR: number;
  dT: number;
}

export interface BurstEngineOptions {
  hue: number;
  theme: CanvasBackgroundTheme;
  color: string | null;
  count: number;
  getType: () => BurstType | "none";
}

export interface BurstEngine {
  spawn: (origin: BurstOrigin, count?: number, typeOverride?: BurstType) => void;
  destroy: () => void;
}

function createColorFn(hue: number, theme: CanvasBackgroundTheme, color: string | null): ColorFn {
  let rgb: [number, number, number] | null = null;
  if (color) {
    let hex = color.replace("#", "");
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((char) => char + char)
        .join("");
    }
    rgb = [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
  }

  return (alpha: number): string => {
    if (rgb) {
      return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
    }
    switch (theme) {
      case "dark":
        return `hsla(${hue}, 78%, 78%, ${alpha})`;
      case "light":
        return `hsla(${hue}, 65%, 52%, ${alpha})`;
      case "darkdull":
        return `hsla(${hue}, 22%, 68%, ${alpha * 0.85})`;
      case "lightdull":
        return `hsla(${hue}, 18%, 52%, ${alpha * 0.85})`;
      default:
        return `hsla(${hue}, 65%, 52%, ${alpha})`;
    }
  };
}

function heartPath(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  s: number,
  dL: number,
  dR: number,
  dT: number
): void {
  const tipY = cy + s * (0.9 + dT * 0.05);
  const notchY = cy - s * (0.65 - dT * 0.05);
  const eqY = cy - s * 0.28;
  const lobeTopY = cy - s * (1.0 + dT * 0.08);
  const rX = cx + s * (1.0 + dR * 0.1);
  const lX = cx - s * (1.0 + dL * 0.1);
  const rTopX = cx + s * (0.4 + dR * 0.06);
  const lTopX = cx - s * (0.4 + dL * 0.06);
  ctx.beginPath();
  ctx.moveTo(cx, tipY);
  ctx.bezierCurveTo(cx - s * (0.4 + dL * 0.05), cy + s * 0.5, lX, cy + s * 0.1, lX, eqY);
  ctx.bezierCurveTo(lX, lobeTopY, lTopX, lobeTopY, cx, notchY);
  ctx.bezierCurveTo(rTopX, lobeTopY, rX, lobeTopY, rX, eqY);
  ctx.bezierCurveTo(rX, cy + s * 0.1, cx + s * (0.4 + dR * 0.05), cy + s * 0.5, cx, tipY);
  ctx.closePath();
}

function starPath(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number): void {
  const outer = r;
  const inner = r * 0.1;
  ctx.beginPath();
  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2;
    const distance = i % 2 === 0 ? outer : inner;
    const px = cx + Math.cos(angle) * distance;
    const py = cy + Math.sin(angle) * distance;
    if (i === 0) {
      ctx.moveTo(px, py);
    } else {
      ctx.lineTo(px, py);
    }
  }
  ctx.closePath();
}

function rrect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

function drawHeart(ctx: CanvasRenderingContext2D, tc: ColorFn, p: BurstParticle, alpha: number, scale: number): void {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.rot);
  ctx.scale(scale, scale);
  heartPath(ctx, 0, 0, p.size, p.dL, p.dR, p.dT);
  ctx.fillStyle = tc(alpha);
  ctx.fill();
  ctx.restore();
}

function drawSparkle(ctx: CanvasRenderingContext2D, tc: ColorFn, p: BurstParticle, alpha: number, scale: number): void {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.rot);
  ctx.scale(scale, scale);
  const glowRadius = p.size * 1.6;
  const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, glowRadius);
  gradient.addColorStop(0, tc(alpha * 0.3));
  gradient.addColorStop(1, tc(0));
  ctx.beginPath();
  ctx.arc(0, 0, glowRadius, 0, Math.PI * 2);
  ctx.fillStyle = gradient;
  ctx.fill();
  starPath(ctx, 0, 0, p.size);
  ctx.fillStyle = tc(alpha);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(0, 0, p.size * 0.3, 0, Math.PI * 2);
  ctx.fillStyle = tc(Math.min(1, alpha * 1.5));
  ctx.fill();
  ctx.restore();
}

function drawNote(ctx: CanvasRenderingContext2D, tc: ColorFn, p: BurstParticle, alpha: number, scale: number): void {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.rot);
  ctx.scale(scale, scale);
  ctx.font = `${p.size}px serif`;
  ctx.fillStyle = tc(alpha);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(p.char, 0, 0);
  ctx.restore();
}

function drawCard(ctx: CanvasRenderingContext2D, tc: ColorFn, p: BurstParticle, alpha: number, scale: number): void {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.rot);
  ctx.scale(scale, scale);
  const w = p.size;
  const h = w * 1.42;
  const r = w * 0.1;
  rrect(ctx, -w / 2, -h / 2, w, h, r);
  ctx.fillStyle = tc(alpha * 0.15);
  ctx.fill();
  ctx.strokeStyle = tc(alpha);
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.font = `${p.size * 0.6}px serif`;
  ctx.fillStyle = tc(alpha * 0.9);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(p.suit, 0, 0);
  ctx.restore();
}

function drawDie(ctx: CanvasRenderingContext2D, tc: ColorFn, p: BurstParticle, alpha: number, scale: number): void {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.rot);
  ctx.scale(scale, scale);
  const s = p.size;
  const r = s * 0.14;
  rrect(ctx, -s / 2, -s / 2, s, s, r);
  ctx.fillStyle = tc(alpha * 0.15);
  ctx.fill();
  ctx.strokeStyle = tc(alpha);
  ctx.lineWidth = 1.5;
  ctx.stroke();
  const dotRadius = s * 0.085;
  const offset = s * 0.27;
  ctx.fillStyle = tc(alpha * 0.9);
  for (const [dx, dy] of DOT_OFFSETS[p.face]) {
    ctx.beginPath();
    ctx.arc(dx * offset, dy * offset, dotRadius, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawNode(ctx: CanvasRenderingContext2D, tc: ColorFn, p: BurstParticle, alpha: number, scale: number): void {
  const glowRadius = p.size * 5.5;
  const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, glowRadius);
  gradient.addColorStop(0, tc(alpha * 0.2));
  gradient.addColorStop(1, tc(0));
  ctx.beginPath();
  ctx.arc(p.x, p.y, glowRadius, 0, Math.PI * 2);
  ctx.fillStyle = gradient;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(p.x, p.y, p.size * scale, 0, Math.PI * 2);
  ctx.fillStyle = tc(alpha);
  ctx.fill();
}

const RENDERERS: Record<
  ParticleRenderType,
  (ctx: CanvasRenderingContext2D, tc: ColorFn, p: BurstParticle, alpha: number, scale: number) => void
> = {
  heart: drawHeart,
  sparkle: drawSparkle,
  note: drawNote,
  card: drawCard,
  die: drawDie,
  node: drawNode,
};

function alphaFor(p: BurstParticle, t: number): number {
  if (t < 0.1) {
    return (t / 0.1) * PEAK_ALPHA;
  }
  if (t > p.fadeStart) {
    const fade = (1 - t) / (1 - p.fadeStart);
    return fade * fade * PEAK_ALPHA;
  }
  return PEAK_ALPHA;
}

function scaleFor(p: BurstParticle, t: number): number {
  if (t < 0.1) {
    return (t / 0.1) * 1.18;
  }
  if (t < 0.18) {
    return 1.18 - 0.18 * ((t - 0.1) / 0.08);
  }
  return 1 + Math.sin(p.life * 0.18) * 0.04;
}

type LegacyBurstType = Exclude<BurstType, ThemedBurstType>;

const THEMED_BURST_TYPES: ReadonlySet<string> = new Set<string>(Object.keys(THEMED_BURST_COUNT));

function isThemedBurstType(type: BurstType): type is ThemedBurstType {
  return THEMED_BURST_TYPES.has(type);
}

function isThemedParticle(p: BurstParticle | ThemedBurstParticle): p is ThemedBurstParticle {
  return THEMED_BURST_TYPES.has(p.type);
}

function resolveParticleRenderType(type: LegacyBurstType): ParticleRenderType {
  if (type === "card-die") {
    return Math.random() < 0.5 ? "card" : "die";
  }
  if (type === "connector") {
    return "node";
  }
  return type;
}

function createParticle(renderType: ParticleRenderType, x: number, y: number): BurstParticle {
  const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.2;
  const speed = 1.0 + Math.random() * 1.2;
  const delay = -Math.floor(Math.random() * 18);
  const jitterX = (Math.random() - 0.5) * 30;
  const jitterY = (Math.random() - 0.5) * 10;
  const [sizeMin, sizeMax] = TYPE_SIZE[renderType];

  return {
    type: renderType,
    x: x + jitterX,
    y: y + jitterY,
    vx: Math.cos(angle) * speed * 0.6 + (Math.random() - 0.5) * 0.4,
    vy: Math.sin(angle) * speed * 0.7 - 0.75,
    size: sizeMin + Math.random() * (sizeMax - sizeMin),
    rot: (Math.random() - 0.5) * 0.6,
    rotV: (Math.random() - 0.5) * 0.04,
    wob: Math.random() * Math.PI * 2,
    wobAmp: 0.6 + Math.random() * 0.6,
    life: delay,
    maxLife: 70 + Math.random() * 60,
    fadeStart: 0.62 + Math.random() * 0.22,
    suit: SUITS[Math.floor(Math.random() * 4)],
    face: 1 + Math.floor(Math.random() * 6),
    char: NOTE_CHARS[Math.floor(Math.random() * 4)],
    dL: (Math.random() - 0.5) * 0.28,
    dR: (Math.random() - 0.5) * 0.28,
    dT: (Math.random() - 0.5) * 0.18,
  };
}

function drawConnectors(
  ctx: CanvasRenderingContext2D,
  tc: ColorFn,
  particles: (BurstParticle | ThemedBurstParticle)[]
): void {
  const liveNodes = particles.filter((p): p is BurstParticle => p.type === "node" && p.life >= 0);
  if (liveNodes.length <= 1) {
    return;
  }
  for (let i = 0; i < liveNodes.length; i++) {
    for (let j = i + 1; j < liveNodes.length; j++) {
      const a = liveNodes[i];
      const b = liveNodes[j];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance >= CONNECTOR_MAX_DISTANCE) {
        continue;
      }
      const linkAlpha =
        (1 - distance / CONNECTOR_MAX_DISTANCE) ** 1.4 *
        0.6 *
        Math.min(alphaFor(a, a.life / a.maxLife), alphaFor(b, b.life / b.maxLife));
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.strokeStyle = tc(linkAlpha);
      ctx.lineWidth = 1.1;
      ctx.stroke();
    }
  }
}

function advanceParticle(p: BurstParticle): void {
  p.life++;
  p.x += p.vx + Math.sin(p.life * 0.1 + p.wob) * 0.12 * p.wobAmp;
  p.y += p.vy;
  p.vy += 0.012;
  p.vx *= 0.985;
  p.rot += p.rotV;
}

function renderFrame(
  ctx: CanvasRenderingContext2D,
  tc: ColorFn,
  particles: (BurstParticle | ThemedBurstParticle)[],
  widthCss: number,
  heightCss: number
): void {
  ctx.clearRect(0, 0, widthCss, heightCss);
  drawConnectors(ctx, tc, particles);

  for (let i = particles.length - 1; i >= 0; i--) {
    const p = particles[i];
    if (p.life < 0) {
      p.life++;
      continue;
    }
    if (isThemedParticle(p)) {
      drawThemedParticle(ctx, tc, p);
      advanceThemedParticle(p);
    } else {
      const t = p.life / p.maxLife;
      RENDERERS[p.type](ctx, tc, p, alphaFor(p, t), scaleFor(p, t));
      advanceParticle(p);
    }
    if (p.life >= p.maxLife) {
      particles.splice(i, 1);
    }
  }
}

export function createBurstEngine(canvas: HTMLCanvasElement, options: BurstEngineOptions): BurstEngine | null {
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return null;
  }

  const dpr = window.devicePixelRatio || 1;
  const tc = createColorFn(options.hue, options.theme, options.color);
  const particles: (BurstParticle | ThemedBurstParticle)[] = [];
  let rafId: number | null = null;
  const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

  const view = canvas.ownerDocument.defaultView ?? window;

  const resize = (): void => {
    const widthCss = Math.min(view.innerWidth || 1, SAFE_MAX_CANVAS_DIMENSION);
    const heightCss = Math.min(view.innerHeight || 1, SAFE_MAX_CANVAS_DIMENSION);
    canvas.width = Math.max(1, Math.floor(widthCss * dpr));
    canvas.height = Math.max(1, Math.floor(heightCss * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };
  resize();

  view.addEventListener("resize", resize);

  const loop = (): void => {
    renderFrame(ctx, tc, particles, canvas.width / dpr, canvas.height / dpr);
    rafId = particles.length > 0 ? window.requestAnimationFrame(loop) : null;
  };

  const spawn = (origin: BurstOrigin, count?: number, typeOverride?: BurstType): void => {
    const type = typeOverride ?? options.getType();
    if (type === "none" || reducedMotionQuery.matches) {
      return;
    }
    if (isThemedBurstType(type)) {
      for (let i = 0; i < (count ?? THEMED_BURST_COUNT[type]); i++) {
        particles.push(createThemedParticle(type, origin));
      }
    } else {
      for (let i = 0; i < (count ?? options.count); i++) {
        particles.push(createParticle(resolveParticleRenderType(type), origin.x, origin.y));
      }
    }
    if (rafId === null) {
      rafId = window.requestAnimationFrame(loop);
    }
  };

  const destroy = (): void => {
    view.removeEventListener("resize", resize);
    if (rafId !== null) {
      window.cancelAnimationFrame(rafId);
      rafId = null;
    }
    particles.length = 0;
  };

  return { spawn, destroy };
}
